// Utility functions for country/city handling
import i18n from '../i18n/index.js';

const SUPPORTED = ['en', 'es', 'fr'];
const indexByLang = {}; // lang -> index json (or null if it failed to load)

// Normalize the active i18n language to a folder we ship (strips region like es-ES)
const baseLang = () => {
  const l = (i18n.language || 'en').split('-')[0];
  return SUPPORTED.includes(l) ? l : 'en';
};

// Fetch + cache the cities index for a specific language.
const fetchIndex = async (lang) => {
  if (lang in indexByLang) return indexByLang[lang];
  try {
    const response = await fetch(`./TRIP/${lang}/cities-index.json`);
    indexByLang[lang] = response.ok ? await response.json() : null;
    if (indexByLang[lang]) {
      console.log(`✅ Loaded cities index (${lang}): ${indexByLang[lang].totalCities} cities`);
    }
  } catch (error) {
    console.error(`❌ Error loading cities index (${lang}):`, error);
    indexByLang[lang] = null;
  }
  return indexByLang[lang];
};

// Active-language index — used for showing localized city/country suggestions.
// (City AND country names are localized per language; the language-agnostic root
// index is English-only and must not be used for lookups.)
export const loadCitiesIndex = async () => {
  return (await fetchIndex(baseLang())) || (baseLang() !== 'en' ? await fetchIndex('en') : null);
};

// Resolve a country name (in ANY supported language) to its file id.
// Trips store the country name localized to the language they were created in, so
// after switching language the stored name won't be in the active index — we must
// search all languages, otherwise viewing the trip loses all its city data.
export const getCountryFile = async (countryName) => {
  if (!countryName) return null;
  const key = countryName.toLowerCase();
  const langs = [baseLang(), ...SUPPORTED.filter(l => l !== baseLang())];
  for (const lang of langs) {
    const idx = await fetchIndex(lang);
    if (!idx) continue;
    const file = idx.countryFileMap?.[key]
      || idx.countries?.find(c => c.name.toLowerCase() === key)?.file;
    if (file) return file;
  }
  return null;
};

// Get all cities for a country
export const getCitiesForCountry = async (countryName) => {
  const idx = await loadCitiesIndex();
  if (!idx) return [];

  return idx.cities.filter(city =>
    city.country.toLowerCase() === countryName.toLowerCase()
  );
};

// Search cities by name
export const searchCities = async (query, limit = 5) => {
  const idx = await loadCitiesIndex();
  if (!idx || !query) return [];

  return idx.cities
    .filter(city => city.city.toLowerCase().includes(query.toLowerCase()))
    .slice(0, limit);
};

// Search countries by name
export const searchCountries = async (query, limit = 5) => {
  const idx = await loadCitiesIndex();
  if (!idx || !query) return [];

  return idx.countries
    .filter(country => country.name.toLowerCase().includes(query.toLowerCase()))
    .slice(0, limit);
};

// Legacy function for backward compatibility
export const getCountryCode = async (countryName) => {
  return await getCountryFile(countryName);
};